import { useState, useCallback } from "react";

export interface PeerReview {
  id: string;
  postId: number;
  rating: number;
  comment: string;
  author: string;
  date: string;
}

interface PeerFeedbackStore {
  reviews: PeerReview[];
}

const DEFAULT: PeerFeedbackStore = { reviews: [] };

function load(): PeerFeedbackStore {
  try {
    const raw = localStorage.getItem("growseo_peer_feedback");
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn("PeerFeedback load error", e);
  }
  return DEFAULT;
}

function save(store: PeerFeedbackStore) {
  try {
    localStorage.setItem("growseo_peer_feedback", JSON.stringify(store));
  } catch (e) {
    console.warn("PeerFeedback save error", e);
  }
}

export function usePeerFeedback() {
  const [data, setData] = useState<PeerFeedbackStore>(load);

  const addReview = useCallback((review: Omit<PeerReview, "id" | "date">) => {
    const newReview: PeerReview = {
      ...review,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: new Date().toLocaleDateString("ko-KR"),
    };
    setData((prev) => {
      const next = { reviews: [...prev.reviews, newReview] };
      save(next);
      return next;
    });
  }, []);

  const getReviewsForPost = useCallback(
    (postId: number) => data.reviews.filter((r) => r.postId === postId),
    [data.reviews]
  );

  const hasReviewedPost = useCallback(
    (postId: number, author: string) =>
      data.reviews.some((r) => r.postId === postId && r.author === author),
    [data.reviews]
  );

  return { addReview, getReviewsForPost, hasReviewedPost };
}
